(() => {
  if (window.AeockyHomepageController) {
    window.AeockyHomepageController.boot(document);
    return;
  }

  const instances = new WeakMap();
  const roots = new Set();
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const desktop = () => window.matchMedia('(min-width: 1025px)').matches;
  const behavior = () => (reduceMotion() ? 'auto' : 'smooth');

  const backgroundValue = (value) => {
    if (!value) return 'none';
    const safeValue = value.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/[\n\r\f]/g, '');
    return `url("${safeValue}")`;
  };

  const setupDeferredBackground = (root, signal) => {
    const desktopBackground = root.dataset.aeBackgroundDesktop;
    const tabletBackground = root.dataset.aeBackgroundTablet || desktopBackground;
    const mobileBackground = root.dataset.aeBackgroundMobile || tabletBackground;
    if (!desktopBackground && !tabletBackground && !mobileBackground) return () => {};

    let activated = false;
    const activate = () => {
      if (activated) return;
      activated = true;
      root.style.setProperty('--ae-home-bg-ready', backgroundValue(desktopBackground));
      root.style.setProperty('--ae-home-bg-tablet-ready', backgroundValue(tabletBackground));
      root.style.setProperty('--ae-home-bg-mobile-ready', backgroundValue(mobileBackground));
      root.dataset.aeBackgroundReady = 'true';
      observer?.disconnect();
    };
    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) activate();
      }, { rootMargin: '500px 0px', threshold: 0 })
      : null;

    if (observer) observer.observe(root);
    else activate();

    document.addEventListener('shopify:section:select', (event) => {
      if (event.target?.contains?.(root) || root.contains(event.target)) activate();
    }, { signal });
    document.addEventListener('shopify:block:select', (event) => {
      if (root.contains(event.target)) activate();
    }, { signal });

    return () => observer?.disconnect();
  };

  const createCycle = (root, callback, signal, delay = 5200, enabled = true) => {
    let timer;
    let paused = false;
    const stop = () => {
      window.clearInterval(timer);
      timer = undefined;
    };
    const start = () => {
      stop();
      if (enabled && !paused && desktop() && !reduceMotion()) timer = window.setInterval(callback, delay);
    };
    root.addEventListener('mouseenter', () => { paused = true; stop(); }, { signal });
    root.addEventListener('mouseleave', () => { paused = false; start(); }, { signal });
    root.addEventListener('focusin', () => { paused = true; stop(); }, { signal });
    root.addEventListener('focusout', (event) => {
      if (root.contains(event.relatedTarget)) return;
      paused = false;
      start();
    }, { signal });
    start();
    return { start, stop };
  };

  const stepRail = (rail, direction) => {
    const card = rail.firstElementChild;
    if (!card) return;
    const gap = Number.parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap) || 0;
    rail.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: behavior() });
  };

  const setupSolutions = (root, signal) => {
    const rail = root.querySelector('[data-ae-solution-rail]');
    const cards = [...root.querySelectorAll('[data-ae-solution]')];
    if (!rail || !cards.length) return;
    const activate = (index) => {
      const bounded = Math.max(0, Math.min(index, cards.length - 1));
      rail.dataset.activeIndex = String(bounded);
      cards.forEach((card, cardIndex) => {
        const active = cardIndex === bounded;
        card.classList.toggle('is-active', active);
        card.setAttribute('aria-current', active ? 'true' : 'false');
      });
    };
    cards.forEach((card, index) => {
      card.addEventListener('pointerenter', () => { if (desktop()) activate(index); }, { signal });
      card.addEventListener('focusin', () => { if (desktop()) activate(index); }, { signal });
      card.addEventListener('click', () => { if (desktop()) activate(index); }, { signal });
    });
    activate(Number.parseInt(rail.dataset.activeIndex || '0', 10));
  };

  const setupPress = (root, signal) => {
    const rail = root.querySelector('[data-ae-press-rail]');
    if (!rail) return () => {};
    const originalItems = [...rail.children];
    const autoplay = root.dataset.autoplay !== 'false';
    const speed = Math.max(10, Number.parseFloat(root.dataset.marqueeSpeed || '28'));
    const pauseReasons = new Set();
    const clones = [];
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    let animationFrame;
    let interactionTimer;
    let previousTime;
    let loopWidth = 0;
    let position = rail.scrollLeft;

    const stop = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = undefined;
      previousTime = undefined;
    };

    const removeClones = () => {
      stop();
      if (loopWidth > 0) rail.scrollLeft %= loopWidth;
      position = rail.scrollLeft;
      clones.splice(0).forEach((clone) => clone.remove());
      loopWidth = 0;
      delete root.dataset.aeMarqueeActive;
    };

    const buildClones = () => {
      if (clones.length || originalItems.length < 2) return;
      originalItems.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.classList.add('is-ae-marquee-clone');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('id');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
        clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((node) => node.setAttribute('tabindex', '-1'));
        rail.append(clone);
        clones.push(clone);
      });
      loopWidth = clones[0]?.offsetLeft - originalItems[0]?.offsetLeft;
      if (loopWidth > 0) {
        position = rail.scrollLeft % loopWidth;
        rail.scrollLeft = position;
        root.dataset.aeMarqueeActive = 'true';
      }
    };

    const canRun = () => autoplay && !motionQuery.matches && originalItems.length > 1;

    const tick = (time) => {
      if (!canRun() || pauseReasons.size || loopWidth <= 0) {
        stop();
        return;
      }
      if (previousTime === undefined) previousTime = time;
      const elapsed = Math.min(time - previousTime, 64);
      previousTime = time;
      position += speed * elapsed / 1000;
      if (position >= loopWidth) position %= loopWidth;
      rail.scrollLeft = position;
      animationFrame = window.requestAnimationFrame(tick);
    };

    const start = () => {
      stop();
      if (!canRun() || pauseReasons.size) return;
      buildClones();
      if (loopWidth > 0) {
        position = rail.scrollLeft % loopWidth;
        animationFrame = window.requestAnimationFrame(tick);
      }
    };

    const pause = (reason) => {
      pauseReasons.add(reason);
      stop();
    };
    const resume = (reason) => {
      pauseReasons.delete(reason);
      start();
    };

    if (hoverQuery.matches) {
      rail.addEventListener('mouseenter', () => pause('hover'), { signal });
      rail.addEventListener('mouseleave', () => resume('hover'), { signal });
    }
    root.addEventListener('focusin', () => pause('focus'), { signal });
    root.addEventListener('focusout', (event) => {
      if (!root.contains(event.relatedTarget)) resume('focus');
    }, { signal });
    rail.addEventListener('pointerdown', () => {
      window.clearTimeout(interactionTimer);
      pause('pointer');
    }, { signal });
    const resumeAfterInteraction = () => {
      window.clearTimeout(interactionTimer);
      interactionTimer = window.setTimeout(() => resume('pointer'), 1400);
    };
    window.addEventListener('pointerup', resumeAfterInteraction, { signal });
    window.addEventListener('pointercancel', resumeAfterInteraction, { signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) pause('hidden');
      else resume('hidden');
    }, { signal });
    window.addEventListener('pagehide', () => pause('page'), { signal });
    window.addEventListener('pageshow', () => resume('page'), { signal });

    const visibilityObserver = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) resume('offscreen');
          else pause('offscreen');
        });
      }, { rootMargin: '200px 0px', threshold: 0 })
      : null;
    if (visibilityObserver) {
      pauseReasons.add('offscreen');
      visibilityObserver.observe(root);
    }

    const handleMotionChange = () => {
      if (motionQuery.matches) removeClones();
      else start();
    };
    motionQuery.addEventListener?.('change', handleMotionChange);
    start();

    return () => {
      window.clearTimeout(interactionTimer);
      motionQuery.removeEventListener?.('change', handleMotionChange);
      visibilityObserver?.disconnect();
      removeClones();
    };
  };

  const setupUgc = (root, signal) => {
    const rail = root.querySelector('[data-ae-ugc-rail]');
    if (!rail) return () => {};
    const videos = [...rail.querySelectorAll('video')];
    const previousControl = root.querySelector('[data-ae-ugc-prev]');
    const nextControl = root.querySelector('[data-ae-ugc-next]');
    const stepUgcRail = (direction) => {
      const card = rail.firstElementChild;
      if (!card) return;
      const gap = Number.parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap) || 0;
      const step = card.getBoundingClientRect().width + gap;
      const maximum = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const atStart = rail.scrollLeft <= 2;
      const atEnd = rail.scrollLeft >= maximum - 2;
      const wraps = (direction < 0 && atStart) || (direction > 0 && atEnd);
      const target = direction < 0
        ? (atStart ? maximum : Math.max(0, rail.scrollLeft - step))
        : (atEnd ? 0 : Math.min(maximum, rail.scrollLeft + step));
      rail.scrollTo({ left: target, behavior: wraps ? 'auto' : behavior() });
    };
    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && !entry.target.paused) entry.target.pause();
        });
      }, { root: rail, threshold: 0.25 })
      : null;
    previousControl?.addEventListener('click', () => {
      stepUgcRail(-1);
    }, { signal });
    nextControl?.addEventListener('click', () => {
      stepUgcRail(1);
    }, { signal });
    videos.forEach((video) => {
      const card = video.closest('[data-ae-ugc-card]');
      const playButton = card?.querySelector('[data-ae-ugc-play]');
      const syncState = () => {
        const isPlaying = !video.paused && !video.ended;
        card?.classList.toggle('is-playing', isPlaying);
        if (isPlaying) card?.classList.add('has-started');
      };
      const togglePlayback = () => {
        videos.forEach((otherVideo) => {
          if (otherVideo !== video && !otherVideo.paused) otherVideo.pause();
        });
        if (video.paused || video.ended) video.play().catch(() => {});
        else video.pause();
      };
      playButton?.addEventListener('click', togglePlayback, { signal });
      video.addEventListener('click', togglePlayback, { signal });
      video.addEventListener('play', syncState, { signal });
      video.addEventListener('pause', syncState, { signal });
      video.addEventListener('ended', syncState, { signal });
      observer?.observe(video);
      syncState();
    });
    return () => {
      observer?.disconnect();
      videos.forEach((video) => video.pause());
    };
  };

  const setupTestimonial = (root, signal) => {
    const rail = root.querySelector('[data-ae-testimonial-rail]');
    const copies = [...root.querySelectorAll('[data-ae-testimonial-copy]')];
    const media = [...root.querySelectorAll('[data-ae-testimonial-item]')];
    const length = Math.min(copies.length, media.length);
    if (!rail || !length) return () => {};
    let index = 0;
    let scrollFrame;
    let transitionFrame;
    let transitionTimer;
    let transitionState;
    let queuedDirection = 0;
    const setActive = (value) => {
      index = (value + length) % length;
      copies.forEach((item, itemIndex) => {
        const active = itemIndex === index;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      media.forEach((item, itemIndex) => {
        const active = itemIndex === index;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-current', active ? 'true' : 'false');
      });
    };
    const show = (value, shouldScroll = false) => {
      setActive(value);
      if (shouldScroll && media[index]) rail.scrollTo({ left: media[index].offsetLeft, behavior: behavior() });
    };
    const mediaIndex = (item) => Number.parseInt(item?.dataset.aeTestimonialItem || '0', 10);
    const alignDesktopOrder = () => {
      const activeItem = media[index];
      let safety = length;
      while (rail.firstElementChild && rail.firstElementChild !== activeItem && safety > 0) {
        rail.append(rail.firstElementChild);
        safety -= 1;
      }
      rail.scrollLeft = 0;
    };
    const desktopStep = () => {
      const first = rail.firstElementChild;
      const second = first?.nextElementSibling;
      if (first && second) return Math.abs(second.getBoundingClientRect().left - first.getBoundingClientRect().left);
      const gap = Number.parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap) || 0;
      return first ? first.getBoundingClientRect().width + gap : 0;
    };
    const finishDesktopTransition = () => {
      if (!transitionState) return;
      window.clearTimeout(transitionTimer);
      window.cancelAnimationFrame(transitionFrame);
      const { direction, shiftedItem } = transitionState;
      rail.classList.remove('is-animating');
      if (direction > 0 && shiftedItem) rail.append(shiftedItem);
      rail.style.transform = '';
      transitionState = undefined;
      setActive(mediaIndex(rail.firstElementChild));
      if (queuedDirection) {
        const nextDirection = Math.sign(queuedDirection);
        queuedDirection -= nextDirection;
        transitionFrame = window.requestAnimationFrame(() => moveDesktop(nextDirection));
      }
    };
    const moveDesktop = (direction) => {
      if (transitionState) {
        queuedDirection += direction;
        return;
      }
      alignDesktopOrder();
      const shiftedItem = direction > 0 ? rail.firstElementChild : rail.lastElementChild;
      if (!shiftedItem) return;
      if (reduceMotion()) {
        if (direction > 0) rail.append(shiftedItem);
        else rail.prepend(shiftedItem);
        setActive(mediaIndex(rail.firstElementChild));
        return;
      }
      if (direction < 0) rail.prepend(shiftedItem);
      const step = desktopStep();
      if (!step) return;
      transitionState = { direction, shiftedItem };
      rail.classList.remove('is-animating');
      rail.style.transform = direction > 0 ? 'translate3d(0, 0, 0)' : `translate3d(-${step}px, 0, 0)`;
      rail.getBoundingClientRect();
      rail.classList.add('is-animating');
      transitionFrame = window.requestAnimationFrame(() => {
        rail.style.transform = direction > 0 ? `translate3d(-${step}px, 0, 0)` : 'translate3d(0, 0, 0)';
      });
      transitionTimer = window.setTimeout(finishDesktopTransition, 700);
    };
    const move = (direction) => {
      if (desktop()) moveDesktop(direction);
      else show(index + direction, true);
    };
    rail.addEventListener('transitionend', (event) => {
      if (event.target === rail && event.propertyName === 'transform') finishDesktopTransition();
    }, { signal });
    root.querySelector('[data-ae-testimonial-prev]')?.addEventListener('click', () => move(-1), { signal });
    root.querySelector('[data-ae-testimonial-next]')?.addEventListener('click', () => move(1), { signal });
    media.slice(0, length).forEach((item, itemIndex) => {
      item.addEventListener('click', () => show(itemIndex, !desktop()), { signal });
      item.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          show(itemIndex, !desktop());
        }
      }, { signal });
    });
    rail.addEventListener('scroll', () => {
      if (desktop()) return;
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(() => {
        const nearest = media.slice(0, length).reduce((best, item, itemIndex) => {
          const distance = Math.abs(item.offsetLeft - rail.scrollLeft);
          return distance < best.distance ? { index: itemIndex, distance } : best;
        }, { index, distance: Number.POSITIVE_INFINITY });
        if (nearest.index !== index) show(nearest.index);
      });
    }, { signal, passive: true });
    show(0);
    const autoplay = root.dataset.autoplay !== 'false';
    const delay = Number.parseInt(root.dataset.cycleSpeed || '6200', 10);
    const cycle = createCycle(root, () => move(1), signal, delay, autoplay);
    return () => {
      cycle.stop();
      window.cancelAnimationFrame(scrollFrame);
      window.cancelAnimationFrame(transitionFrame);
      window.clearTimeout(transitionTimer);
      rail.classList.remove('is-animating');
      rail.style.transform = '';
    };
  };

  const initialize = (root) => {
    if (!root || instances.has(root)) return;
    const aborter = new AbortController();
    const { signal } = aborter;
    const teardowns = [setupDeferredBackground(root, signal)];
    if (root.dataset.aeHomeSection === 'solutions') setupSolutions(root, signal);
    if (root.dataset.aeHomeSection === 'press') teardowns.push(setupPress(root, signal));
    if (root.dataset.aeHomeSection === 'ugc') teardowns.push(setupUgc(root, signal));
    if (root.dataset.aeHomeSection === 'testimonial') teardowns.push(setupTestimonial(root, signal));
    const destroy = () => {
      teardowns.reverse().forEach((teardown) => teardown());
      aborter.abort();
      instances.delete(root);
      roots.delete(root);
    };
    instances.set(root, { destroy });
    roots.add(root);
  };

  const within = (node) => node?.matches?.('[data-ae-home-section]')
    ? [node]
    : [...(node?.querySelectorAll?.('[data-ae-home-section]') || [])];
  const boot = (node = document) => within(node).forEach(initialize);
  const unload = (node) => within(node).forEach((root) => instances.get(root)?.destroy());
  let resizeTimer;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      const activeRoots = [...roots];
      activeRoots.forEach((root) => instances.get(root)?.destroy());
      activeRoots.filter((root) => root.isConnected).forEach(initialize);
    }, 160);
  });
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target));
  window.AeockyHomepageController = { boot, unload };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
})();
